/* global React */
import { Box, lighten } from '@mui/material'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Avatar, { type AvatarEditProps } from 'ui/Element/Image/Avatar'

/**
 * Returns the AvatarEdit element's current theme styles.
 *
 * The `button` and `buttonIcon` classes belong to the edit button, which is
 * currently disabled (SP-1509).
 *
 * @param theme the application's current theme
 * @returns     the AvatarEdit element's styles
 */
const styles = (theme: any) => ({
  root: {
    position: 'relative',
    aspectRatio: '1 / 1',
  },
  button: {
    position: 'absolute',
    cursor: 'pointer',
    transition: 'background-color 250ms ease',
    borderRadius: '50%',
    borderColor: 'transparent',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: 'none',
    minWidth: 0,
    width: '2.625rem',
    height: '2.625rem',
    right: 0,
    bottom: 0,
    '&:hover': {
      backgroundColor: lighten(theme.palette.secondary.main, 0.3),
      textDecoration: 'none',
      '@media (hover: none)': {
        backgroundColor: lighten(theme.palette.secondary.main, 0.3),
      },
    },
    '&:active': {
      boxShadow: 'none',
    },
  },
  buttonIcon: {
    color: theme.palette.text.inverted,
  },
  ...theme.custom.avatarEditImage,
})

/**
 * AvatarEdit Image Element
 *
 * Displays an avatar contained in a circle together with a button intended
 * to edit it.
 *
 * TODO: The edit button (FabButton with the `button`/`buttonIcon` classes and
 *       an `events.onClick` handler) is disabled until it works (SP-1509).
 *       Setting, changing or deleting the avatar also doesn't reflect until
 *       the page is refreshed; an earlier attempt tried to solve this by
 *       adding the new image to an attachment group (see git history).
 *
 * @param props              the element's properties, forwarded to {@link Avatar}
 * @param props.ref          a reference to the root element
 * @param props.value        the id of the avatar's owner
 * @param props.api          api information used to fetch the avatar
 * @param props.width        element's width, defaults to 100%
 * @param props.events       event handlers for the edit button (disabled)
 * @returns                  the new avatar edit element
 */
const AvatarEdit = ({ ref, value, ...props }: AvatarEditProps) => {
  const { width } = props
  const classes = useStyles(styles)() as Record<string, string>

  return (
    <Box
      ref={ref}
      className={classes.root}
      sx={{ width }}
    >
      <Avatar
        {...props}
        value={value}
        fallbackIcon={{ size: '70%' }}
      />
      {/* TODO: commented out until we make it work (SP-1509) */}
      {/* <FabButton */}
      {/*   events={events} */}
      {/*   color={'white.main'} */}
      {/*   icon={{ name: 'edit', variant: 'filled' }} */}
      {/*   classes={{ */}
      {/*     root: classes.button, */}
      {/*     icon: classes.buttonIcon, */}
      {/*   }} */}
      {/* /> */}
    </Box>
  )
}

export default useMemoRef(AvatarEdit, (props: AvatarEditProps) => [props.src, props.value])
